import {
  type PilotLine,
  type SimilarSuggestion,
  SUGGESTION_STATUSES,
  type SuggestionDetail,
  type TicketDetail,
  type TicketUrgency,
} from '@epc/contracts';
import * as RD from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  ArrowUpRight,
  Bug,
  Camera,
  CheckCircle2,
  EyeOff,
  LifeBuoy,
  Lightbulb,
  Link2,
  Megaphone,
  MessageSquareText,
  Paperclip,
  Pencil,
  RefreshCw,
  Square,
  Undo2,
  X,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useMe } from '../lib/session';
import { type ReportKind, supportMeQuery, supportUi, useSupportUi } from '../lib/support';
import { ReportPilot } from '../pages/support/ReportPilot';
import { uploadSupportFiles } from '../pages/support/SupportDialogs';
import { Button } from '../ui/button';
import { ErrorBox } from '../ui/feedback';
import type { Crumb } from './crumbs';
import {
  canvasFromImage,
  canvasToJpeg,
  captureViewport,
  currentDevice,
  deviceLine,
  drawMarks,
  errorsReport,
  listenPageErrors,
  type Mark,
  type MarkTool,
  normalizeMark,
  projectIdFrom,
  recentErrors,
  renderMarked,
  reportPagePath,
  subjectFrom,
  textShowsMoney,
} from './report-capture';

/*
 * Кнопка «Сообщить» на каждой странице (БЗ-21, БЗ-22, БЗ-28; как в портале АГП): ошибка, просьба о помощи или
 * доработка одним текстом. Страница, проект, снимок экрана, сведения о браузере и ошибки страницы прикладываются
 * сами. Ошибка и «нужна помощь» — обращение специалисту поддержки (ОБР-…, сроки первого ответа БЗ-24),
 * доработка — предложение (ПРД-…), за которое коллеги голосуют «+1 мне тоже». Ответ приходит в «Ждёт меня» и
 * по почте или в Telegram по настройкам человека. Кнопка — в шапке; на телефоне — круглая внизу справа; на
 * компьютере ещё клавиша «?».
 */

listenPageErrors();

const KINDS: Array<{ value: ReportKind; label: string; icon: ReactNode; tone: string }> = [
  {
    value: 'bug',
    label: 'Ошибка',
    icon: <Bug className="size-5" aria-hidden />,
    tone: 'has-[:checked]:border-danger has-[:checked]:bg-danger-soft has-[:checked]:text-danger',
  },
  {
    value: 'question',
    label: 'Нужна помощь',
    icon: <LifeBuoy className="size-5" aria-hidden />,
    tone: 'has-[:checked]:border-accent has-[:checked]:bg-accent-soft has-[:checked]:text-accent-text',
  },
  {
    value: 'idea',
    label: 'Доработка',
    icon: <Lightbulb className="size-5" aria-hidden />,
    tone: 'has-[:checked]:border-warning has-[:checked]:bg-warning-soft has-[:checked]:text-warning',
  },
];

const TEXT_LABEL: Record<ReportKind, string> = {
  bug: 'Что случилось',
  question: 'С чем помочь',
  idea: 'Что хотите улучшить и зачем',
};

const TEXT_HINT: Record<ReportKind, string> = {
  bug: 'Что делали, что ожидали и что получилось',
  question: 'Например: не вижу акт за сентябрь, как согласовать заявку',
  idea: 'Что добавить или изменить на этой странице и чем это поможет',
};

const DRAFT_KEY = 'epc:report-draft:';

function draftGet(path: string): string {
  try {
    return sessionStorage.getItem(DRAFT_KEY + path) ?? '';
  } catch {
    return '';
  }
}

function draftSet(path: string, text: string) {
  try {
    if (text) sessionStorage.setItem(DRAFT_KEY + path, text);
    else sessionStorage.removeItem(DRAFT_KEY + path);
  } catch {
    // хранилище браузера недоступно — черновик живёт, пока открыта страница
  }
}

/** Путь страницы, как его видит человек: «Договоры › ДГ-0012 › вкладка «Оплаты»» */
function pageTitleOf(trail: Crumb[]): string {
  const parts = trail.map((c) => (c.title ?? c.label).trim()).filter((l) => l && l !== '…');
  const tabs = document.querySelectorAll('main [role="tab"][aria-selected="true"]');
  const tab = tabs.length ? tabs[tabs.length - 1]?.textContent?.replace(/\s+/g, ' ').trim() : '';
  if (tab && !parts.includes(tab)) parts.push(`вкладка «${tab}»`);
  return parts.join(' › ').slice(0, 200) || document.title.slice(0, 200);
}

const isEditable = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el?.tagName) return false;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable;
};

function useCanReport(): boolean {
  const me = useMe();
  // Поддержка — для сотрудников компании; внешние участники пишут своему контактному лицу (как и раньше)
  return Boolean(me.tenant?.staff);
}

/** Сколько ответов поддержки и новых статусов предложений ждёт человека */
function useUnread(): number {
  const can = useCanReport();
  const q = useQuery({ ...supportMeQuery, enabled: can });
  return q.data?.unread ?? 0;
}

/**
 * Кнопка в шапке — на широком экране; на телефоне и планшете шапка занята поиском и Pilot, там «Сообщить» — круглая
 * кнопка внизу справа. Подпись «Сообщить» — с 1440 px, уже — один значок, чтобы строка «где вы находитесь» не пропала
 */
export function ReportButton() {
  const can = useCanReport();
  const unread = useUnread();
  if (!can) return null;
  return (
    <button
      type="button"
      onClick={() => supportUi.openReport()}
      className="relative hidden h-10 shrink-0 items-center gap-2 rounded-md border border-line-strong px-2.5 text-[14px] font-medium text-ink hover:bg-tile xl:inline-flex min-[1440px]:px-3"
      aria-label="Сообщить: ошибка, нужна помощь, доработка"
      title="Сообщить об ошибке, попросить помощи или предложить доработку этой страницы (клавиша ?)"
    >
      <Megaphone className="size-5 text-accent-text" aria-hidden />
      <span className="hidden min-[1440px]:inline">Сообщить</span>
      {unread ? (
        <span
          className="num absolute -top-1.5 -right-1.5 min-w-4 rounded-sm bg-accent px-1 text-center text-[10px] leading-4 font-semibold text-on-accent"
          title="Есть ответ поддержки"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </button>
  );
}

/** Круглая кнопка на телефоне и планшете: прячется, пока человек печатает и пока открыта панель */
function ReportFab({ hidden }: { hidden: boolean }) {
  const unread = useUnread();
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const on = (e: FocusEvent) => setTyping(isEditable(e.target));
    const off = () => setTyping(false);
    document.addEventListener('focusin', on);
    document.addEventListener('focusout', off);
    return () => {
      document.removeEventListener('focusin', on);
      document.removeEventListener('focusout', off);
    };
  }, []);
  if (hidden || typing) return null;
  return (
    <button
      type="button"
      onClick={() => supportUi.openReport()}
      data-report-ui=""
      className="fixed right-4 bottom-[calc(16px+env(safe-area-inset-bottom))] z-30 inline-flex size-12 items-center justify-center rounded-full bg-accent text-on-accent shadow-md xl:hidden print:hidden"
      aria-label="Сообщить: ошибка, нужна помощь, доработка"
    >
      <Megaphone className="size-5" aria-hidden />
      {unread ? (
        <span className="num absolute -top-1 -right-1 min-w-4 rounded-sm bg-ink px-1 text-center text-[10px] leading-4 font-semibold text-on-ink">
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </button>
  );
}

interface Shot {
  base: HTMLCanvasElement;
  marks: Mark[];
}

interface Sent {
  kind: ReportKind;
  id: string;
  number: string;
}

/** Панель «Сообщить», круглая кнопка на телефоне, клавиша «?» и уведомление об отправке */
export function ReportPanel({ trail }: { trail: Crumb[] }) {
  const can = useCanReport();
  const { report: open, reportKind } = useSupportUi();
  const [sent, setSent] = useState<Sent | null>(null);

  // «?» на компьютере открывает панель, если человек не печатает и не открыто другое окно
  useEffect(() => {
    if (!can) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey || isEditable(e.target)) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      supportUi.openReport();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [can]);

  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(null), 10_000);
    return () => clearTimeout(t);
  }, [sent]);

  // Новое сообщение — прежнее уведомление об отправке не закрывает кнопки панели
  useEffect(() => {
    if (open) setSent(null);
  }, [open]);

  if (!can) return null;
  return (
    <>
      <ReportFab hidden={open} />
      <RD.Root open={open} onOpenChange={(v) => (v ? supportUi.openReport() : supportUi.closeReport())}>
        <RD.Portal>
          <RD.Overlay data-report-ui="" className="fixed inset-0 z-50 bg-ink/30" />
          <RD.Content
            data-report-ui=""
            aria-describedby={undefined}
            className="fixed inset-x-0 bottom-0 z-50 flex h-[92dvh] flex-col rounded-t-lg border-line bg-surface shadow-md md:inset-y-0 md:right-0 md:left-auto md:h-auto md:w-[460px] md:rounded-none md:border-l"
          >
            {open ? (
              <ReportBody
                trail={trail}
                initialKind={reportKind}
                onSent={(s) => {
                  setSent(s);
                  supportUi.closeReport();
                }}
              />
            ) : null}
          </RD.Content>
        </RD.Portal>
      </RD.Root>
      {sent ? <SentNotice sent={sent} onClose={() => setSent(null)} /> : null}
    </>
  );
}

function SentNotice({ sent, onClose }: { sent: Sent; onClose: () => void }) {
  const idea = sent.kind === 'idea';
  return (
    <div
      role="status"
      data-report-ui=""
      className="fixed inset-x-4 bottom-[calc(80px+env(safe-area-inset-bottom))] z-[60] flex items-start gap-3 rounded-lg bg-ink px-4 py-3 text-[14px] text-on-ink shadow-md md:inset-x-auto md:right-6 md:max-w-[400px] xl:bottom-6"
    >
      <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-accent" aria-hidden />
      <div className="min-w-0 flex-1">
        <Link
          to={idea ? '/support/suggestions/$id' : '/support/tickets/$id'}
          params={{ id: sent.id }}
          onClick={onClose}
          className="font-semibold underline underline-offset-2"
        >
          {idea ? 'Предложение' : 'Обращение'} {sent.number} отправлено
        </Link>
        <p className="mt-0.5 text-on-ink/80">
          {idea
            ? 'Коллеги смогут его поддержать, статус придёт в «Ждёт меня». '
            : 'Ответ придёт в «Ждёт меня» и на почту или в Telegram, как настроено в профиле. '}
          <Link
            to="/support"
            search={(idea ? { tab: 'suggestions' } : {}) as never}
            onClick={onClose}
            className="underline underline-offset-2"
          >
            {idea ? 'Все предложения' : 'Мои обращения'}
          </Link>
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-on-ink/80 hover:text-on-ink md:size-8"
        aria-label="Закрыть уведомление"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

function ReportBody({
  trail,
  initialKind,
  onSent,
}: {
  trail: Crumb[];
  initialKind: ReportKind | null;
  onSent: (s: Sent) => void;
}) {
  const qc = useQueryClient();
  const location = useRouterState({ select: (s) => s.location });
  // Страница — на момент открытия панели: та, о которой человек сообщает
  const [page] = useState(() => ({
    path: reportPagePath(location.pathname, location.searchStr ?? ''),
    key: location.pathname,
    title: pageTitleOf(trail),
    projectId: projectIdFrom(location.pathname),
  }));
  const [kind, setKind] = useState<ReportKind>(initialKind ?? 'bug');
  // Переписка с Pilot по «Нужна помощь» (БЗ-21, БЗ-22): уходит в обращение
  const [pilotLines, setPilotLines] = useState<PilotLine[]>([]);
  const [text, setText] = useState(() => draftGet(page.key));
  const [urgency, setUrgency] = useState<TicketUrgency>('normal');
  const [shot, setShot] = useState<Shot | null>(null);
  const [shotState, setShotState] = useState<'taking' | 'ready' | 'failed'>('taking');
  const [money] = useState(() => textShowsMoney(document.querySelector('main')?.innerText ?? ''));
  const [withShot, setWithShot] = useState(!money);
  const [withTech, setWithTech] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [editing, setEditing] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const idea = kind === 'idea';

  const take = useCallback(() => {
    setShotState('taking');
    captureViewport()
      .then((base) => {
        setShot({ base, marks: [] });
        setShotState('ready');
      })
      .catch(() => {
        setShot(null);
        setShotState('failed');
      });
  }, []);

  // Снимок — сразу при открытии: панель и затемнение в него не попадают
  useEffect(() => {
    take();
  }, [take]);

  // На компьютере сразу можно печатать; на телефоне клавиатура закрыла бы половину панели
  useEffect(() => {
    if (window.matchMedia?.('(pointer: coarse)').matches) return;
    const t = setTimeout(() => textRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, []);

  // Снимок из буфера обмена (Ctrl+V) — вместо снятого порталом
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((x) => x.type.startsWith('image/'));
      const blob = item?.getAsFile();
      if (!blob) return;
      e.preventDefault();
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        setShot({ base: canvasFromImage(img), marks: [] });
        setShotState('ready');
        setWithShot(true);
        URL.revokeObjectURL(url);
      };
      img.onerror = () => URL.revokeObjectURL(url);
      img.src = url;
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  const thumb = useMemo(
    () => (shot ? renderMarked(shot.base, shot.marks).toDataURL('image/jpeg', 0.7) : null),
    [shot],
  );

  // Похожие доработки этой страницы и раздела — «+1 мне тоже» вместо второго такого же предложения
  const [words, setWords] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setWords(subjectFrom(text)), 500);
    return () => clearTimeout(t);
  }, [text]);
  const similarKey = ['support', 'report-similar', page.path, words.length >= 3 ? words : ''] as const;
  const similar = useQuery({
    queryKey: similarKey,
    queryFn: () =>
      api<SimilarSuggestion[]>('/support/suggestions/similar', {
        query: { path: page.path ?? undefined, q: words.length >= 3 ? words : undefined },
      }),
    enabled: idea && Boolean(page.path || words.length >= 3),
    staleTime: 30_000,
  });
  const vote = useMutation({
    mutationFn: (s: SimilarSuggestion) =>
      api<SuggestionDetail>(`/support/suggestions/${s.id}/vote`, { method: s.voted ? 'DELETE' : 'POST' }),
    onSuccess: (d) => {
      qc.setQueryData<SimilarSuggestion[]>(similarKey, (list) =>
        list?.map((x) => (x.id === d.id ? { ...x, voted: d.voted, votes: d.votes } : x)),
      );
      void qc.invalidateQueries({ queryKey: ['support', 'suggestions'] });
    },
  });

  const send = useMutation({
    mutationFn: async (): Promise<Sent> => {
      const body = text.trim();
      const first = subjectFrom(body);
      const subject = first.length >= 3 ? first : subjectFrom(body.replace(/\s+/g, ' '));
      const files: File[] = [];
      const attach = async (number: string) => {
        if (withShot && shot) {
          const blob = await canvasToJpeg(renderMarked(shot.base, shot.marks));
          if (blob) files.push(new File([blob], `снимок-экрана-${number}.jpg`, { type: 'image/jpeg' }));
        }
        if (file) files.push(file);
      };
      if (kind === 'idea') {
        const s = await api<SuggestionDetail>('/support/suggestions', {
          method: 'POST',
          body: { title: subject, proposal: body, pagePath: page.path },
        });
        await attach(s.number);
        if (files.length) await uploadSupportFiles('support_suggestion', s.id, files);
        return { kind, id: s.id, number: s.number };
      }
      const device = currentDevice();
      const ticket = (projectId: string | null) =>
        api<TicketDetail>('/support/tickets', {
          method: 'POST',
          body: {
            subject,
            kind,
            urgency: kind === 'bug' ? urgency : 'normal',
            body,
            pagePath: page.path,
            pageTitle: page.title,
            // Сняли галочку — сведения о браузере не отправляются вовсе (пустая строка, а не адрес браузера)
            device: withTech ? deviceLine(device) : '',
            projectId,
            pilotTranscript: kind === 'question' && pilotLines.length ? pilotLines : undefined,
          },
        });
      let t: TicketDetail;
      try {
        t = await ticket(page.projectId);
      } catch (e) {
        // Сообщают как раз о том, что проект не открывается: обращение уходит без ссылки на проект
        if (!(page.projectId && e instanceof ApiError && e.status === 403 && e.code === 'forbidden')) throw e;
        t = await ticket(null);
      }
      await attach(t.number);
      const errors = withTech ? recentErrors() : [];
      if (errors.length)
        files.push(
          new File([errorsReport(device, page.path ?? page.key, errors)], `ошибки-страницы-${t.number}.txt`, {
            type: 'text/plain',
          }),
        );
      if (files.length) await uploadSupportFiles('support_ticket', t.id, files);
      return { kind, id: t.id, number: t.number };
    },
    onSuccess: (s) => {
      draftSet(page.key, '');
      void qc.invalidateQueries({ queryKey: ['support'] });
      onSent(s);
    },
  });

  function submit() {
    if (text.trim().length < 3) {
      setInvalid(true);
      textRef.current?.focus();
      return;
    }
    setInvalid(false);
    send.mutate();
  }

  const fieldErrors = send.error instanceof ApiError ? send.error.fieldErrors() : {};
  // У панели одно поле текста: ошибки отдельных полей (тема, текст, адрес страницы) — одним сообщением
  const error = send.error
    ? send.error instanceof ApiError && Object.keys(fieldErrors).length
      ? new Error(Object.values(fieldErrors).join(' '))
      : send.error
    : null;

  return (
    <>
      <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <RD.Title className="text-[17px] leading-6 font-semibold text-ink">Сообщить о странице</RD.Title>
          <p className="mt-0.5 truncate text-[13px] text-ink-2" title={page.title}>
            {page.title}
          </p>
        </div>
        <RD.Close
          className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-tile hover:text-ink md:m-0 md:size-8"
          aria-label="Закрыть"
        >
          <X className="size-5" />
        </RD.Close>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-[14px]">
        <fieldset>
          <legend className="sr-only">Что случилось</legend>
          <div className="grid grid-cols-3 gap-2">
            {KINDS.map((k) => (
              <label
                key={k.value}
                className={clsx(
                  'flex min-h-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-[1.5px] border-line px-1 py-2 text-center text-[13px] leading-4 text-ink-2 has-[:checked]:font-semibold has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus',
                  k.tone,
                )}
              >
                <input
                  type="radio"
                  name="report-kind"
                  value={k.value}
                  checked={kind === k.value}
                  onChange={() => setKind(k.value)}
                  className="sr-only"
                />
                {k.icon}
                <span>{k.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <label htmlFor="report-text" className="mt-4 mb-1.5 block text-[13px] font-medium text-ink">
          {TEXT_LABEL[kind]}
        </label>
        <textarea
          ref={textRef}
          id="report-text"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            draftSet(page.key, e.target.value);
            if (invalid && e.target.value.trim().length >= 3) setInvalid(false);
          }}
          rows={5}
          maxLength={4000}
          placeholder={TEXT_HINT[kind]}
          aria-invalid={invalid || undefined}
          className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-[14px] leading-5 text-ink placeholder:text-ink-3 hover:border-ink-3 focus:border-ink focus:outline-none aria-[invalid=true]:border-danger"
        />
        {invalid ? (
          <p className="mt-1 text-[12px] text-danger" role="alert">
            Напишите хотя бы пару слов: {TEXT_HINT[kind].toLowerCase()}.
          </p>
        ) : null}

        {kind === 'question' ? (
          <ReportPilot
            question={text}
            path={page.path}
            onTranscript={setPilotLines}
            onResolved={() => draftSet(page.key, '')}
          />
        ) : null}

        {kind === 'bug' ? (
          <fieldset className="mt-3">
            <legend className="mb-1.5 text-[13px] font-medium text-ink">Насколько мешает</legend>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['blocking', 'Не могу работать'],
                  ['normal', 'Мешает, но обхожу'],
                ] as const
              ).map(([v, label]) => (
                <label
                  key={v}
                  className="inline-flex min-h-11 cursor-pointer items-center rounded-full border border-line-strong px-3.5 text-[13px] text-ink has-[:checked]:border-ink has-[:checked]:bg-ink has-[:checked]:text-on-ink has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-focus md:min-h-8"
                >
                  <input
                    type="radio"
                    name="report-urgency"
                    value={v}
                    checked={urgency === v}
                    onChange={() => setUrgency(v)}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-[12px] text-ink-2">
              {urgency === 'blocking' ? 'Ответим в течение 2 часов.' : 'Ответим в течение 4 рабочих часов.'}
            </p>
          </fieldset>
        ) : null}

        {idea && similar.data?.length ? (
          <div className="mt-4 rounded-md border border-warning/40 bg-warning-soft px-3 py-2.5">
            <p className="text-[13px] font-medium text-warning">
              Похожие пожелания уже есть — поддержите их вместо нового
            </p>
            <ul className="mt-1 flex flex-col">
              {similar.data.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 border-t border-dashed border-warning/30 py-1.5 first:border-t-0"
                >
                  <div className="min-w-0">
                    <Link
                      to="/support/suggestions/$id"
                      params={{ id: s.id }}
                      onClick={() => supportUi.closeReport()}
                      className="text-[13px] text-ink [overflow-wrap:anywhere] hover:underline"
                    >
                      {s.number} · {s.title}
                    </Link>
                    <div className="text-[12px] text-ink-2">
                      {SUGGESTION_STATUSES[s.status]}
                      {s.samePage ? ' · с этой страницы' : ''} · поддержали: {s.votes}
                      {s.mine ? ' · ваше' : ''}
                    </div>
                  </div>
                  {s.mine ? null : (
                    <button
                      type="button"
                      onClick={() => vote.mutate(s)}
                      disabled={vote.isPending}
                      aria-pressed={s.voted}
                      className={clsx(
                        'inline-flex h-11 shrink-0 items-center rounded-full border px-3 text-[12px] font-medium whitespace-nowrap md:h-7',
                        s.voted
                          ? 'border-warning bg-warning text-surface'
                          : 'border-warning bg-surface text-warning',
                      )}
                    >
                      {s.voted ? 'Вы поддержали' : '+1 мне тоже'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-4 rounded-md border border-line bg-surface-2 px-3 py-3">
          <p className="text-[12px] font-semibold text-ink-2">Приложится само</p>
          <div className="mt-2 flex items-start gap-2 text-[13px]">
            <Link2 className="mt-0.5 size-4 shrink-0 text-ink-2" aria-hidden />
            <span className="w-16 shrink-0 text-ink-2">Страница</span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{page.title}</span>
          </div>
          <div className="mt-2 flex items-start gap-2 text-[13px]">
            <Camera className="mt-0.5 size-4 shrink-0 text-ink-2" aria-hidden />
            <span className="w-16 shrink-0 text-ink-2">Снимок</span>
            <span className="min-w-0">экрана — как вы его видите</span>
          </div>
          <div className="mt-2 flex items-start gap-3">
            <button
              type="button"
              onClick={() => shot && setEditing(true)}
              disabled={!shot}
              className="flex h-[96px] w-[150px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-line-strong bg-surface"
              aria-label="Обвести место на снимке"
            >
              {thumb ? (
                <img src={thumb} alt="Снимок экрана" className="h-full w-full object-cover object-top" />
              ) : (
                <span className="px-2 text-[12px] text-ink-2">
                  {shotState === 'taking'
                    ? 'Снимаю экран…'
                    : 'Снимок не получился: приложите файл или вставьте снимок'}
                </span>
              )}
            </button>
            <div className="flex min-w-0 flex-col items-start">
              <ShotAction
                icon={<Pencil className="size-4" />}
                onClick={() => setEditing(true)}
                disabled={!shot}
              >
                Обвести место
              </ShotAction>
              <ShotAction
                icon={<RefreshCw className="size-4" />}
                onClick={take}
                disabled={shotState === 'taking'}
              >
                Снять заново
              </ShotAction>
              <ShotAction icon={<Paperclip className="size-4" />} onClick={() => fileRef.current?.click()}>
                {file ? 'Другой файл' : 'Добавить файл'}
              </ShotAction>
              <span className="hidden text-[12px] text-ink-2 md:block">или вставьте снимок: Ctrl+V</span>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf,.txt,.xlsx,.docx"
            className="hidden"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          {file ? (
            <div className="mt-2 flex items-center gap-2 text-[13px]">
              <Paperclip className="size-4 shrink-0 text-ink-2" aria-hidden />
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{file.name}</span>
              <button
                type="button"
                onClick={() => setFile(null)}
                aria-label={`Убрать файл ${file.name}`}
                className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-tile md:size-8"
              >
                <X className="size-4" />
              </button>
            </div>
          ) : null}
          <Check checked={withShot} onChange={setWithShot} disabled={!shot}>
            Приложить снимок{idea ? ' — его увидят все сотрудники' : ''}
          </Check>
          {money ? (
            <p className="mt-1 rounded-md bg-warning-soft px-2.5 py-2 text-[12px] text-warning">
              На странице есть суммы, поэтому снимок выключен. Если он нужен, включите его и закройте суммы:
              «Обвести место» → «Скрыть данные».
            </p>
          ) : null}
          {idea ? null : (
            <Check checked={withTech} onChange={setWithTech}>
              Сведения о браузере, размере экрана и ошибках страницы
            </Check>
          )}
        </div>
        {error ? <ErrorBox error={error} className="mt-3" /> : null}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
        <Button variant="primary" loading={send.isPending} onClick={submit}>
          Отправить
        </Button>
        <Button variant="ghost" onClick={() => supportUi.closeReport()}>
          Отмена
        </Button>
        <span className="basis-full text-[12px] leading-4 text-ink-2 md:ml-auto md:max-w-[200px] md:basis-auto md:text-right">
          {idea
            ? 'Предложение увидят все сотрудники и смогут поддержать'
            : 'Видите вы и поддержка портала, ответ — в «Ждёт меня»'}
        </span>
      </div>

      {shot ? (
        <ShotEditor
          open={editing}
          shot={shot}
          onClose={() => setEditing(false)}
          onDone={(marks) => {
            setShot({ base: shot.base, marks });
            setWithShot(true);
            setEditing(false);
          }}
        />
      ) : null}
    </>
  );
}

function ShotAction({
  icon,
  children,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-11 items-center gap-1.5 text-[13px] text-accent-text hover:underline disabled:text-ink-3 disabled:no-underline md:min-h-7"
    >
      {icon}
      {children}
    </button>
  );
}

function Check({
  checked,
  onChange,
  disabled,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="mt-1 flex min-h-11 cursor-pointer items-center gap-2.5 text-[13px] text-ink md:min-h-8">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 shrink-0 cursor-pointer accent-[var(--color-ink)]"
      />
      <span>{children}</span>
    </label>
  );
}

const TOOLS: Array<{ value: MarkTool; label: string; short: string; icon: ReactNode }> = [
  { value: 'rect', label: 'Рамка', short: 'Рамка', icon: <Square className="size-4" /> },
  { value: 'arrow', label: 'Стрелка', short: 'Стрелка', icon: <ArrowUpRight className="size-4" /> },
  { value: 'text', label: 'Подпись', short: 'Подпись', icon: <MessageSquareText className="size-4" /> },
  // На телефоне подписи короче: инструменты и «Готово» помещаются в две строки над снимком
  { value: 'hide', label: 'Скрыть данные', short: 'Скрыть', icon: <EyeOff className="size-4" /> },
];

/** Пометки на снимке: рамка, стрелка, подпись и «Скрыть данные» — закрасить суммы и лишнее */
function ShotEditor({
  open,
  shot,
  onClose,
  onDone,
}: {
  open: boolean;
  shot: Shot;
  onClose: () => void;
  onDone: (marks: Mark[]) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<MarkTool>('rect');
  const [marks, setMarks] = useState<Mark[]>(shot.marks);
  const [caption, setCaption] = useState('');
  const drag = useRef<Mark | null>(null);
  const captionRef = useRef<HTMLInputElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: при каждом открытии — пометки снимка заново
  useEffect(() => {
    if (open) setMarks(shot.marks);
  }, [open]);

  const paint = useCallback(
    (extra?: Mark) => {
      const c = canvasRef.current;
      const ctx = c?.getContext('2d');
      if (!c || !ctx) return;
      if (c.width !== shot.base.width) c.width = shot.base.width;
      if (c.height !== shot.base.height) c.height = shot.base.height;
      drawMarks(ctx, shot.base, extra ? [...marks, extra] : marks);
    },
    [marks, shot.base],
  );

  useEffect(() => {
    if (!open) return;
    // Холст появляется после открытия окна
    const t = requestAnimationFrame(() => paint());
    return () => cancelAnimationFrame(t);
  }, [open, paint]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * c.width) / r.width, y: ((e.clientY - r.top) * c.height) / r.height };
  };

  return (
    <RD.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <RD.Portal>
        <RD.Content
          data-report-ui=""
          aria-describedby={undefined}
          className="fixed inset-0 z-[70] flex flex-col bg-[#0F1113] text-white"
        >
          <RD.Title className="sr-only">Обвести место на снимке</RD.Title>
          <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 md:px-4">
            <span className="mr-1 hidden text-[14px] md:inline">Обведите, где проблема</span>
            {TOOLS.map((t) => (
              <button
                key={t.value}
                type="button"
                aria-pressed={tool === t.value}
                onClick={() => {
                  setTool(t.value);
                  if (t.value === 'text') setTimeout(() => captionRef.current?.focus(), 0);
                }}
                className={clsx(
                  'inline-flex h-11 items-center gap-1.5 rounded-md border px-2.5 text-[13px] md:h-9',
                  tool === t.value ? 'border-danger bg-danger text-white' : 'border-white/35 text-white',
                )}
              >
                {t.icon}
                <span className="sm:hidden">{t.short}</span>
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setMarks((m) => m.slice(0, -1))}
              disabled={!marks.length}
              aria-label="Отменить последнюю пометку"
              title="Отменить последнюю пометку"
              className="inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-md border border-white/35 px-2.5 text-[13px] text-white disabled:opacity-40 md:h-9"
            >
              <Undo2 className="size-4" aria-hidden />
              <span className="hidden sm:inline">Отменить</span>
            </button>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-11 items-center rounded-md border border-white/35 px-3 text-[13px] text-white md:h-9"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => onDone(marks)}
                className="inline-flex h-11 items-center rounded-md bg-accent px-4 text-[13px] font-semibold text-on-accent md:h-9"
              >
                Готово
              </button>
            </div>
          </div>
          {tool === 'text' ? (
            <div className="px-3 pb-2 md:px-4">
              <input
                ref={captionRef}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={120}
                placeholder="Подпись к месту, затем нажмите на снимок"
                aria-label="Подпись к месту"
                className="h-11 w-full max-w-[420px] rounded-md border border-white/35 bg-white px-3 text-[14px] text-[#15171A] md:h-9"
              />
            </div>
          ) : null}
          <div className="flex min-h-0 flex-1 items-center justify-center px-3 pb-3 md:px-4 md:pb-4">
            <canvas
              ref={canvasRef}
              className="max-h-full max-w-full cursor-crosshair rounded-md bg-white"
              style={{ touchAction: 'none' }}
              onPointerDown={(e) => {
                const p = pos(e);
                if (tool === 'text') {
                  const mark = normalizeMark({ t: 'text', x: p.x, y: p.y, w: 0, h: 0, s: caption });
                  if (mark) {
                    setMarks((m) => [...m, mark]);
                    setCaption('');
                  } else captionRef.current?.focus();
                  return;
                }
                e.currentTarget.setPointerCapture(e.pointerId);
                drag.current = { t: tool, x: p.x, y: p.y, w: 0, h: 0 };
              }}
              onPointerMove={(e) => {
                if (!drag.current) return;
                const p = pos(e);
                drag.current = { ...drag.current, w: p.x - drag.current.x, h: p.y - drag.current.y };
                paint(drag.current);
              }}
              onPointerUp={() => {
                const m = drag.current ? normalizeMark(drag.current) : null;
                drag.current = null;
                if (m) setMarks((list) => [...list, m]);
                else paint();
              }}
              onPointerCancel={() => {
                drag.current = null;
                paint();
              }}
            />
          </div>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
